import { AxiosResponse } from "axios";
import { createAxiosInstance } from "./AxiosService";

const axiosInstance = createAxiosInstance();

export interface AstroLocation {
  lat: number;
  lon: number;
  height: number;
}

export interface AstrolabeRequest {
  date: string;
  lat?: number;
  lon?: number;
  height?: number;
}

export interface CelestialPosition {
  name: string;
  azimuth: number;
  altitude: number;
  ecliptic_lon?: number;
  ecliptic_lat?: number;
  distance?: number;
  magnitude?: number;
  constellation?: string;
}

export interface EclipticIntersection {
  ascending: {
    azimuth: number;
    time?: string;
  };
  descending: {
    azimuth: number;
    time?: string;
  };
}

export interface AstrolabeResponse {
  date: string;
  location: AstroLocation;
  positions: CelestialPosition[];
  intersections: EclipticIntersection;
}

export class AstroService {
  // Default location coordinates (Zurich, Switzerland)
  private static readonly DEFAULT_LAT = 47.36667;
  private static readonly DEFAULT_LON = 8.55;
  private static readonly DEFAULT_HEIGHT = 0;

  /**
   * Get astrolabe positions for celestial bodies
   * @param date - Date in ISO format (YYYY-MM-DD)
   * @param lat - Latitude (default: Zurich)
   * @param lon - Longitude (default: Zurich)
   * @param height - Height above sea level in meters (default: 0)
   */
  static async getAstrolabePositions(
    date: string,
    lat: number = this.DEFAULT_LAT,
    lon: number = this.DEFAULT_LON,
    height: number = this.DEFAULT_HEIGHT,
  ): Promise<AstrolabeResponse> {
    try {
      const response: AxiosResponse<AstrolabeResponse> =
        await axiosInstance.post("/astro/astrolabe", {
          date,
          lat,
          lon,
          height,
        });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to fetch astrolabe positions",
      );
    }
  }

  /**
   * Get astrolabe positions for current date and location
   */
  static async getCurrentAstrolabePositions(
    lat?: number,
    lon?: number,
    height?: number,
  ): Promise<AstrolabeResponse> {
    const currentDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
    return this.getAstrolabePositions(currentDate, lat, lon, height);
  }

  /**
   * Get astrolabe positions for a specific location
   */
  static async getAstrolabePositionsForLocation(
    date: string,
    location: AstroLocation,
  ): Promise<AstrolabeResponse> {
    return this.getAstrolabePositions(
      date,
      location.lat,
      location.lon,
      location.height,
    );
  }

  /**
   * Validate date/time format (YYYY-MM-DD or ISO 8601)
   */
  static isValidDate(date: string): boolean {
    const dateTimeRegex =
      /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/;
    if (!dateTimeRegex.test(date)) {
      return false;
    }

    const parsedDate = new Date(date);
    return parsedDate instanceof Date && !isNaN(parsedDate.getTime());
  }

  /**
   * Validate coordinates
   */
  static isValidCoordinates(lat: number, lon: number): boolean {
    return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
  }

  /**
   * Format date for API request
   */
  static formatDate(date: Date): string {
    return date.toISOString().split("T")[0];
  }

  /**
   * Get positions for multiple dates (batch request)
   */
  static async getAstrolabePositionsForDateRange(
    startDate: string,
    endDate: string,
    lat?: number,
    lon?: number,
    height?: number,
  ): Promise<AstrolabeResponse[]> {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (start > end) {
      throw new Error("Start date must be before end date");
    }

    const dayDiff = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );

    // Limit to prevent too many requests
    if (dayDiff > 30) {
      throw new Error("Date range cannot exceed 30 days");
    }

    const requests: Promise<AstrolabeResponse>[] = [];

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateString = this.formatDate(d);
      requests.push(this.getAstrolabePositions(dateString, lat, lon, height));
    }

    try {
      return await Promise.all(requests);
    } catch (error) {
      console.error("Failed to fetch positions for date range:", error);
      throw error;
    }
  }

  /**
   * Find visible planets for a given date and location
   */
  static async getVisiblePlanets(
    date: string,
    lat?: number,
    lon?: number,
    height?: number,
  ): Promise<CelestialPosition[]> {
    try {
      const response = await this.getAstrolabePositions(date, lat, lon, height);

      // Filter for planets (assuming they have specific naming conventions)
      const planetNames = [
        "mercury",
        "venus",
        "mars",
        "jupiter",
        "saturn",
        "uranus",
        "neptune",
      ];

      return response.positions.filter(
        (position) =>
          planetNames.some((planet) =>
            position.name.toLowerCase().includes(planet),
          ) && position.altitude > 0, // Only visible planets (above horizon)
      );
    } catch (error) {
      console.error("Failed to fetch visible planets:", error);
      throw error;
    }
  }

  /**
   * Get sun and moon positions
   */
  static async getSunMoonPositions(
    date: string,
    lat?: number,
    lon?: number,
    height?: number,
  ): Promise<{ sun?: CelestialPosition; moon?: CelestialPosition }> {
    try {
      const response = await this.getAstrolabePositions(date, lat, lon, height);

      const sun = response.positions.find((position) =>
        position.name.toLowerCase().includes("sun"),
      );

      const moon = response.positions.find((position) =>
        position.name.toLowerCase().includes("moon"),
      );

      return { sun, moon };
    } catch (error) {
      console.error("Failed to fetch sun/moon positions:", error);
      throw error;
    }
  }

  /**
   * Check if service is available (no authentication required)
   */
  static isAvailable(): boolean {
    return true; // Astro service doesn't require authentication
  }

  /**
   * Get ecliptic horizon crossings for a date and location
   */
  static async getEclipticCrossings(
    date: string,
    lat?: number,
    lon?: number,
    height?: number,
  ): Promise<EclipticIntersection> {
    try {
      const response = await this.getAstrolabePositions(date, lat, lon, height);
      return response.intersections;
    } catch (error) {
      console.error("Failed to fetch ecliptic crossings:", error);
      throw error;
    }
  }
}

export default AstroService;
